import React, { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'

function Profile(): React.JSX.Element {
  const { user, changePassword } = useAuth()

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setSuccessMessage('')
    setErrorMessage('')

    if (newPassword !== confirmPassword) {
      setErrorMessage('New passwords do not match.')
      return
    }

    if (newPassword.length < 8) {
      setErrorMessage('New password must be at least 8 characters.')
      return
    }

    setSubmitting(true)
    try {
      await changePassword(currentPassword, newPassword)
      setSuccessMessage('Password updated successfully.')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err: unknown) {
      const error = err as { message?: string }
      setErrorMessage(error?.message || 'Failed to update password. Please check your current password and try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="page">
      <h2>My Profile</h2>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <strong>Account Details</strong>
        </div>
        <div className="card-body">
          <div className="form-group">
            {/* The customer number they can sign in with (DF-375), not the
                portal's internal record id, which means nothing to them. */}
            <label htmlFor="customerNumber" className="form-label">Customer number</label>
            <input
              id="customerNumber"
              type="text"
              className="form-control"
              value={user?.tsg_chargebeeid || ''}
              readOnly
            />
          </div>
          <div className="form-group">
            <label htmlFor="email" className="form-label">Email</label>
            <input
              id="email"
              type="email"
              className="form-control"
              value={user?.email || ''}
              readOnly
            />
          </div>
          <div className="form-group">
            <label htmlFor="name" className="form-label">Name</label>
            <input
              id="name"
              type="text"
              className="form-control"
              value={user?.name || ''}
              readOnly
            />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <strong>Change Password</strong>
        </div>
        <div className="card-body">
          {successMessage && (
            <div className="alert alert-success">{successMessage}</div>
          )}
          {errorMessage && (
            <div className="alert alert-danger">{errorMessage}</div>
          )}
          <form onSubmit={handleChangePassword}>
            <div className="form-group">
              <label htmlFor="currentPassword" className="form-label">Current Password</label>
              <input
                id="currentPassword"
                type="password"
                className="form-control"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>
            <div className="form-group">
              <label htmlFor="newPassword" className="form-label">New Password</label>
              <input
                id="newPassword"
                type="password"
                className="form-control"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={8}
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">Confirm New Password</label>
              <input
                id="confirmPassword"
                type="password"
                className="form-control"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
                {submitting ? 'Updating…' : 'Update Password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default Profile
